import { BowArc } from '@/components/ui/BowArc';
import { Eyebrow } from '@/components/ui/Typography';

/**
 * As duas superfícies do aviso "atualize o app". Componentes **puramente
 * apresentacionais e exportados** — sem rede, sem Router, sem plugin — no mesmo
 * espírito do `FallbackPanel` do scanner. É o que os torna testáveis por
 * render, e essa é a única defesa possível contra o modo de falha que já mordeu
 * duas vezes: bug que só aparece no aparelho.
 *
 * Abertura da loja: `<a target="_blank" rel="noreferrer">`, exatamente como o
 * `InstallPrompt`. Dentro do WebView um `<a>` sem alvo navegaria o PRÓPRIO app
 * pra página web da loja; `capacitor.config.ts` não declara
 * `server.allowNavigation`, então a URL externa é entregue ao sistema.
 */

interface UpdateRequiredScreenProps {
  title: string;
  message: string;
  ctaLabel: string;
  storeUrl: string;
  supportUrl: string;
}

/** Bloqueio duro — cobre a tela inteira, inclusive o login. Sem botão de
 *  fechar: se fosse dispensável não seria bloqueio.
 *
 *  A saída pro suporte é OBRIGATÓRIA (§10: todo estado de erro precisa de saída
 *  alternativa pro cliente). Quem for bloqueado por engano — ou tiver um
 *  aparelho que a loja não oferece mais a versão nova — não tem outro caminho
 *  dentro do app. */
export function UpdateRequiredScreen({
  title,
  message,
  ctaLabel,
  storeUrl,
  supportUrl
}: UpdateRequiredScreenProps) {
  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col items-center justify-center overflow-y-auto px-6"
      style={{
        background: '#F1EDE4',
        paddingTop: 'max(1.5rem, env(safe-area-inset-top))',
        paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))'
      }}
      role="dialog"
      aria-modal="true"
    >
      <BowArc size={72} variant="mono" />
      <Eyebrow className="mt-6">Bow Energy</Eyebrow>
      <h1 className="mt-2 text-center text-2xl font-black tracking-tight text-bow-royal">
        {title}
      </h1>
      <p className="mt-3 max-w-sm text-center text-sm leading-relaxed text-bow-royal/70">
        {message}
      </p>
      <a
        href={storeUrl}
        target="_blank"
        rel="noreferrer"
        className="mt-8 inline-flex h-[54px] w-full max-w-sm items-center justify-center rounded-full bg-bow-royal px-6 text-base font-semibold tracking-tight text-bow-cream"
      >
        {ctaLabel}
      </a>
      <a
        href={supportUrl}
        target="_blank"
        rel="noreferrer"
        className="mt-4 text-sm font-semibold text-bow-royal/60 underline"
      >
        Não consigo atualizar — falar com o suporte
      </a>
    </div>
  );
}

interface UpdateAppBannerProps {
  title: string;
  message: string;
  ctaLabel: string;
  storeUrl: string;
  onDismiss: () => void;
}

/** Aviso soft — barra fina FLUTUANTE, ancorada acima da BottomNav.
 *
 *  Flutuante de propósito: um card inline no `<main>` quebraria as rotas de
 *  viewport fixa (`h-dvh` + `overflow-hidden`), que são garantia dura no Bow.
 *  Fora do fluxo, aparece em qualquer tela sem empurrar nada. */
export function UpdateAppBanner({
  title,
  message,
  ctaLabel,
  storeUrl,
  onDismiss
}: UpdateAppBannerProps) {
  return (
    <div
      className="fixed left-4 right-4 z-40 flex items-center gap-3 rounded-2xl border border-bow-blue/20 bg-white p-3 shadow-soft"
      style={{ bottom: 'calc(env(safe-area-inset-bottom) + 5.5rem)' }}
      role="status"
    >
      <span className="text-xl" aria-hidden>
        ⬆️
      </span>
      <div className="min-w-0 flex-1">
        <span className="block text-sm font-black text-bow-royal">{title}</span>
        <span className="block text-xs text-bow-royal/70">{message}</span>
      </div>
      <a
        href={storeUrl}
        target="_blank"
        rel="noreferrer"
        className="h-9 shrink-0 rounded-lg bg-bow-blue px-4 text-sm font-black leading-9 text-white"
      >
        {ctaLabel}
      </a>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dispensar"
        className="text-bow-royal/40 hover:text-bow-royal/70"
      >
        ×
      </button>
    </div>
  );
}
